import deepCopy from 'deepcopy';
import {
	getSavedOrderSearchesRequest,
	newSavedOrderSearchesRequest,
	updateMultipleSavedOrderSearchesRequest,
	updateSingleSavedOrderSearchRequest,
	deleteSavedOrderSearchRequest,
	getSavedOrderSearchCountRequest,
} from '../requests';
import { getClerkId } from '@/core';
import rdbClient from 'rdb-client';

async function init({ state, commit, dispatch }) {
	await dispatch('fetchSavedSearches');
	if (state.autoSearches.length) {
		commit('set', (state) => {
			state.searchInterval = setInterval(() => { dispatch('autoSearch'); }, 60000);
		});
	}
}

async function dismount({ commit }) {
	commit('set', (state) => { clearInterval(state.searchInterval); });
}

async function fetchSavedSearches({ commit, dispatch }) {
	let clerkId = await getClerkId();
	try {
		let response = await getSavedOrderSearchesRequest({ clerkId });
		commit('setSavedSearches', response);
		dispatch('autoSearch');
	} catch (err) {
		throw new Error('failedToFetch');
	}
}

async function getSettingsSavedSearches({ commit }) {
	let clerkId = await getClerkId();
	try {
		let response = await getSavedOrderSearchesRequest({ clerkId });
		commit('set', (state) => state.settingsSavedSearches = response);
	} catch (err) {
		console.log(`Fetch saved order searches failed: ${  err}`);
	}
}

async function autoSearch({ state, commit }) {
	if (state.liveUpdateInProgress) return;
	commit('set', (state) => { state.liveUpdateInProgress = true; });
	// Force request to be allowed if no return after minute
	setTimeout(() => {
		commit('set', (state) => { state.liveUpdateInProgress = false; });
	}, 60000);
	state.autoSearches.forEach(async search => {
		let ordersCount = await getSavedOrderSearchCountRequest(search.id);
		commit('setOrdersCount', { id: search.id, ordersCount: ordersCount });
	});
	commit('set', (state) => { state.liveUpdateInProgress = false; });
}

async function addSavedSearch({ dispatch }, { searchOptions, searchFilter }) {
	let clerkId = await getClerkId();
	let newSavedSearch = {
		clerkId,
		label: searchOptions.label,
		mainMenu: searchOptions.mainMenu,
		isPublic: searchOptions.isPublic,
		autoSearch: searchOptions.autoSearch,
		count: 0,
		timeInterval: {
			before: searchOptions.timeInterval.before,
			after:  searchOptions.timeInterval.useAfter ? searchOptions.timeInterval.after : ''
		},
		dateType: searchFilter.dateType,
		fromDespatchParty: searchOptions.fromDespatchParty || [],
		toDeliveryParty: searchOptions.toDeliveryParty || [],
		customer: searchOptions.customer || [],
		goodsTypes: searchOptions.goodsTypes || [],
		orderTypes: searchOptions.orderTypes || [],
		hasAtd: searchOptions.hasAtd,
		hasAta: searchOptions.hasAta,
		economicalStatus: searchOptions.economicalStatus || [],
		orderStatus: searchOptions.orderStatus || [],
		references: searchOptions.references || [],
		referencesText: searchOptions.referencesText,
		waybillNumber: searchOptions.waybillNumber || [],
		mustHaveInboundEdi: searchOptions.mustHaveInboundEdi,
		inboundEdi: searchOptions.inboundEdi,
		mustHaveDeviation: searchOptions.mustHaveDeviation,
	};

	try {
		let response = await newSavedOrderSearchesRequest(newSavedSearch);
		dispatch('fetchSavedSearches');
		dispatch('autoSearch');
		return response;
	} catch (err) {
		throw new Error('failedToSave');
	}
}

async function updateSavedSearch({ state: { savedSearches }, commit, dispatch }, { id, searchOptions }) {

	let oldSavedSearch = savedSearches.find(s => s.id === searchOptions.id);

	if (!oldSavedSearch || searchOptions.id === -1) throw new Error('failedToSave');

	let newSavedSearch = {
		...deepCopy(oldSavedSearch),
		label: searchOptions.label,
		mainMenu: searchOptions.mainMenu,
		isPublic: searchOptions.isPublic,
		autoSearch: searchOptions.autoSearch,
		timeInterval: {
			before: searchOptions.timeInterval.before,
			after:  searchOptions.timeInterval.useAfter ? searchOptions.timeInterval.after : ''
		},
		dateType: oldSavedSearch.dateType,
		fromDespatchParty: searchOptions.fromDespatchParty || [],
		toDeliveryParty: searchOptions.toDeliveryParty || [],
		customer: searchOptions.customer || [],
		goodsTypes: searchOptions.goodsTypes || [],
		orderTypes: searchOptions.orderTypes || [],
		hasAtd: searchOptions.hasAtd,
		hasAta: searchOptions.hasAta,
		economicalStatus: searchOptions.economicalStatus || [],
		orderStatus: searchOptions.orderStatus || [],
		references: searchOptions.references || [],
		referencesText: searchOptions.referencesText,
		waybillNumber: searchOptions.waybillNumber || [],
		mustHaveInboundEdi: searchOptions.mustHaveInboundEdi,
		inboundEdi: searchOptions.inboundEdi,
		mustHaveDeviation: searchOptions.mustHaveDeviation,
	};

	try {
		let patch = rdbClient.createPatch(oldSavedSearch, newSavedSearch);
		if (!patch.length) {
			throw new Error('emptyPatch');
		}
		await updateSingleSavedOrderSearchRequest(id, patch);
		oldSavedSearch.label = newSavedSearch.label;
		dispatch(`orderSearch/${newSavedSearch.id}/loadSavedSearch`, newSavedSearch, { root: true });
		dispatch(`orderSearch/${newSavedSearch.id}/fetchOrders`, null, { root: true });
		commit('updateSavedSearch', newSavedSearch);

		if (patch.length > 1 || patch[0].path !== '/data/label') {
			let ordersCount = await getSavedOrderSearchCountRequest(id);
			commit('setOrdersCount', { id, ordersCount });
		}
	} catch (err) {
		if (err.message === 'emptyPatch') throw err;
		throw new Error('failedToSave');
	}
}

async function deleteSavedSearch({ dispatch }, searchId) {
	try {
		await deleteSavedOrderSearchRequest(searchId);
		dispatch('fetchSavedSearches');
	} catch (error) {
		throw new Error('failedToDelete');
	}
}

async function updateSavedSearchesOnMainMenu({ state, commit, dispatch }, items) {
	let savedSearches = state.savedSearches.filter(x => x.id !== -1);

	let originals = [];
	let updated = [];
	for (let item of savedSearches) {
		originals.push(deepCopy(item));
		item.mainMenu = false;
		updated.push(deepCopy(item));
	}

	for (let item of items) {
		let updatedItem = updated.find(x => x.id === item.id);
		updatedItem.mainMenu = true;
	}

	let patch = rdbClient.createPatch(originals, updated);

	try {
		let response = await updateMultipleSavedOrderSearchesRequest(patch);
		commit('setSavedSearches', response);
		dispatch('autoSearch');
	} catch (error) {
		console.log(`Failed to update searches: ${  error}`);
	}
}

export default {
	init,
	dismount,
	fetchSavedSearches,
	getSettingsSavedSearches,
	autoSearch,
	addSavedSearch,
	updateSavedSearch,
	deleteSavedSearch,
	updateSavedSearchesOnMainMenu,
};